import React from 'react';
import { SectionInstance } from '../types';
// POC: reuse the Landing Page module's effect catalogue + renderer so all effect
// kinds (procedural / image / children / text) preview correctly. (A full integration
// would relocate these to a shared module — see the porting advisory.)
import { loadEffectComponent, effectIntegration, defaultEffectProps } from '../../LandingPage/revfxCatalogue';
import { buildEffectElement } from '../../LandingPage/LPComponentRenderer';

/** Build the effect props blob from the Site Builder controls — mirrors
 *  site_components/effect_defaults.build_effect_props so the editor preview matches
 *  the published <aime-effect>. */
function buildProps(effectId: string, c: Record<string, any>): Record<string, any> {
  const props: Record<string, any> = { ...defaultEffectProps(effectId) };
  if (c.effect_color) props.color = c.effect_color;
  const integ = effectIntegration(effectId);
  const img = c.image_url;
  if (img) {
    if (integ.imageProps?.length) props[integ.imageProps[0]] = img;
    else if (integ.kind === 'childImage') props.__img = img;
  }
  if (effectId === 'beforeafter') {
    if (c.before_image) props.__before = c.before_image;
    if (c.after_image) props.__after = c.after_image;
  }
  if (effectId === 'transitionpack' || effectId === 'filmstrip' || effectId === 'carouselx') {
    const urls = (Array.isArray(c.images_list) ? c.images_list : [])
      .map((it: any) => it && it.url).filter(Boolean);
    props[effectId === 'transitionpack' ? 'images' : '__items'] = urls;
  }
  if (effectId === 'lottie' && c.lottie_url) props.src = c.lottie_url;
  if (effectId === 'charts') {
    const labels: string[] = [];
    const data: number[] = [];
    (Array.isArray(c.chart_data) ? c.chart_data : []).forEach((r: any) => {
      const v = parseFloat(r && r.value);
      if (!Number.isNaN(v)) { data.push(v); labels.push(String((r && r.label) || '')); }
    });
    props.data = data;
    props.labels = labels;
    delete props.series; // let AnimatedChart derive a single series from `data`
  }
  const text = c.text_content;
  if (text) {
    if (effectId === 'typewriter') props.phrases = String(text).split('\n').filter((s) => s.trim());
    else if (effectId === 'whiteboard') props.__text = text;
  }
  return props;
}

const Effect: React.FC<{ section: SectionInstance }> = ({ section }) => {
  const c = section.controls || {};
  const effectId = c.effect_id || 'snow';
  const minHeight = parseInt(String(c.min_height || '480'), 10) || 480;
  const bg = c.bg_color || '#0b1020';
  const overlayColor = c.overlay_color || '#ffffff';
  const effectProps = React.useMemo(
    () => buildProps(effectId, c),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [effectId, c.effect_color, c.image_url, c.text_content, c.before_image, c.after_image, c.lottie_url, JSON.stringify(c.images_list), JSON.stringify(c.chart_data)],
  );

  const [Comp, setComp] = React.useState<React.ComponentType<any> | null>(null);
  React.useEffect(() => {
    let alive = true;
    setComp(null);
    loadEffectComponent(effectId).then((C) => { if (alive) setComp(() => C); }).catch(() => {});
    return () => { alive = false; };
  }, [effectId]);

  return (
    <section style={{ position: 'relative', display: 'block', minHeight: `${minHeight}px`, overflow: 'hidden', background: bg }}>
      <div style={{ position: 'absolute', inset: 0, zIndex: 0 }} aria-hidden="true">
        <React.Suspense fallback={null}>
          {Comp ? buildEffectElement(Comp, effectId, effectProps, overlayColor) : null}
        </React.Suspense>
      </div>
      <div style={{
        position: 'absolute', inset: 0, zIndex: 1, display: 'flex', flexDirection: 'column',
        justifyContent: 'center', alignItems: 'center', padding: '40px 48px', boxSizing: 'border-box',
        textAlign: 'center', color: overlayColor, pointerEvents: 'none',
      }}>
        <div style={{ width: '100%', maxWidth: '900px' }}>
          {c.overlay_heading ? <h2 style={{ margin: '0 0 12px', fontSize: '2.4rem', fontWeight: 800, lineHeight: 1.15 }}>{c.overlay_heading}</h2> : null}
          {c.overlay_subheading ? <p style={{ margin: 0, fontSize: '1.15rem', lineHeight: 1.5, opacity: 0.95 }}>{c.overlay_subheading}</p> : null}
          {c.show_cta && c.cta_text ? (
            <div style={{ marginTop: 20 }}>
              <a href={c.cta_url || '#'} className="lp-btn" onClick={(e) => e.preventDefault()}
                style={{ display: 'inline-block', padding: '14px 28px', borderRadius: 9999, background: c.cta_color || '#2177FF', color: '#fff', fontWeight: 700, textDecoration: 'none' }}>
                {c.cta_text}
              </a>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
};

export default Effect;
